import { useEffect, useState, useMemo } from "react";
import type * as React from "react";
import { Box, Typography, Grid } from "@mui/material";
import { useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { AxiosError } from "axios";
import {
  serializeAnalyticsError,
  trackFormSubmissionCompleted
} from "analytics";
import { PATHS } from "../../../../constants/pathConstants";
import {
  AGREEMENT_FORM_PRESENTATION_ORDER,
  FORM_IDS
} from "../../../../constants/v3/formConstants";
import {
  selectHasAcceptedCurrentHipaaAuthorization,
  selectHasAcceptedCurrentPrivacyPolicy,
  selectHasAcceptedCurrentTermsAndConditions,
  setFormSubmissions
} from "../../../../store/reducers/formTemplates/FormTemplatesSlice";
import { openGLSnackbar } from "../../../../store/reducers/snackbar/openGLSnackbarSlice";
import PageTitle from "../../../../utils/PageTitle";
import GradientFramedLayout from "../../../v3/layouts/GradientFramedLayout";
import FormTemplateWrapper, {
  FormData
} from "../../../v3/sharedComponents/forms/FormTemplateWrapper";
import LoadingAnimation from "../../../v3/sharedComponents/LoadingAnimation";
import AgreementsUpdatedModal from "../../sharedComponents/modals/AgreementsUpdatedModal";
import {
  postFormSubmission,
  getFormSubmissions
} from "../../../../apis/v3/formTemplatesApi";
import GL_Logo from "../../../../assets/images/GL-Logo.svg";
import { COLORS } from "components/v3/Theme/colors";

interface FormToComplete {
  formId: string;
}

const UpdateAgreementsPage: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const privacyComplete = useSelector(selectHasAcceptedCurrentPrivacyPolicy);
  const termsComplete = useSelector(selectHasAcceptedCurrentTermsAndConditions);
  const hipaaComplete = useSelector(selectHasAcceptedCurrentHipaaAuthorization);

  const formsToComplete = useMemo<FormToComplete[]>(() => {
    const completion: Record<string, boolean> = {
      [FORM_IDS.PRIVACY_POLICY]: privacyComplete,
      [FORM_IDS.TERMS_AND_CONDITIONS]: termsComplete,
      [FORM_IDS.HIPAA_AUTHORIZATION]: hipaaComplete
    };
    return AGREEMENT_FORM_PRESENTATION_ORDER.filter(
      (formId) => !completion[formId]
    ).map((formId) => ({ formId }));
  }, [privacyComplete, termsComplete, hipaaComplete]);

  const [currentFormIndex, setCurrentFormIndex] = useState(0);
  const [collectedFormData, setCollectedFormData] = useState<FormData[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAgreementsModal, setShowAgreementsModal] = useState(true);

  PageTitle("Update Agreements");

  // Scroll to top when form changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentFormIndex]);

  const currentForm = formsToComplete[currentFormIndex];
  const currentFormId = currentForm?.formId;
  const isLastForm = currentFormIndex === formsToComplete.length - 1;

  const handleFormComplete = async (formData: FormData) => {
    const updatedCollectedData = [...collectedFormData, formData];
    setCollectedFormData(updatedCollectedData);

    if (isLastForm) {
      setIsSubmitting(true);

      try {
        for (const data of updatedCollectedData) {
          const submission = await postFormSubmission({
            formId: data.formId,
            formAnswers: data.formAnswers
          });
          if (submission instanceof AxiosError) {
            trackFormSubmissionCompleted({
              formId: data.formId,
              formVersion: data.formVersion,
              outcome: "failure",
              errorType: serializeAnalyticsError(submission)
            });
            throw submission;
          }
          trackFormSubmissionCompleted({
            formId: data.formId,
            formVersion: data.formVersion,
            outcome: "success"
          });
        }

        const response = await getFormSubmissions();
        if (response instanceof AxiosError) {
          throw response;
        }
        dispatch(setFormSubmissions(response.data));
        history.push(PATHS.LANDING_PAGE);
      } catch (error) {
        console.error("Error submitting forms:", error);
        setIsSubmitting(false);

        dispatch(
          openGLSnackbar({
            variant: "error",
            header: "Error submitting forms",
            subText:
              "Sorry, something went wrong when submitting your forms. Please try again."
          })
        );
      }
    } else {
      setCurrentFormIndex(currentFormIndex + 1);
    }
  };

  if (formsToComplete.length === 0) {
    history.push(PATHS.LANDING_PAGE);
  }

  if (!currentFormId) {
    return null;
  }

  if (isSubmitting) {
    return (
      <GradientFramedLayout showHeader={false}>
        <Grid container sx={{ height: "90vh", position: "relative" }}>
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2
            }}
          >
            <LoadingAnimation />
            <Typography variant="body1">Saving your agreements...</Typography>
          </Box>
        </Grid>
      </GradientFramedLayout>
    );
  }

  return (
    <GradientFramedLayout showHeader={false}>
      <AgreementsUpdatedModal
        isOpen={showAgreementsModal}
        onClose={() => setShowAgreementsModal(false)}
      />
      <Box
        sx={{
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: {
            xs: "30px 16px 60px 16px",
            md: "64px 50px 50px 50px"
          },
          width: "100%",
          maxWidth: "900px"
        }}
      >
        {/* Logo */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginBottom: 4
          }}
        >
          <img
            src={GL_Logo}
            alt="Genomic Life Logo"
            style={{ width: "185px" }}
          />
        </Box>

        {/* Form Container */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            boxSizing: "border-box",
            backgroundColor: COLORS.WHITE,
            border: `1px solid ${COLORS.PRIMARY_LIGHTEST}`,
            borderRadius: "30px",
            padding: { xs: "16px 8px", md: "24px 32px", lg: "32px 32px" }
          }}
        >
          <Box sx={{ width: "100%" }}>
            <FormTemplateWrapper
              key={currentFormId}
              formId={currentFormId}
              deferSubmission={true}
              onCompleteWithData={handleFormComplete}
            />
          </Box>
        </Box>
      </Box>
    </GradientFramedLayout>
  );
};

export default UpdateAgreementsPage;
